import { Link, useParams, Navigate } from "react-router-dom";
import { MapPin, Calendar, Clock, ArrowRight, MessageCircle, CheckCircle2, Camera } from "lucide-react";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import Reveal from "../components/Reveal";
import SectionLabel from "../components/SectionLabel";
import { useSite, resolveImg } from "../context/SiteContext";

export default function DestinationDetail() {
  const { slug } = useParams();
  const { destinations, packages, brand } = useSite();
  const d = destinations.find((x) => x.slug === slug);
  if (!d) return <Navigate to="/destinations" replace />;

  const matchingPackages = packages.filter((p) => (d.packageIds || []).includes(p.id));
  const nearbyDests = (d.nearby || [])
    .map((n) =>
      destinations.find(
        (x) => x.name.toLowerCase() === n.toLowerCase() || x.slug === n.toLowerCase().replace(/\s+/g, "-")
      )
    )
    .filter(Boolean)
    .slice(0, 4);

  const waMsg = encodeURIComponent(
    `Hi ${brand.name}! I'd like to know more about ${d.name} (${d.state}). Could you share an itinerary and quote?`
  );
  const waUrl = `https://wa.me/91${brand.whatsapp}?text=${waMsg}`;
  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(d.mapQuery || `${d.name}, ${d.state}, India`)}&output=embed`;

  return (
    <main data-testid={`destination-detail-${d.slug}`} className="pb-24">
      {/* HERO */}
      <section className="relative h-[70vh] min-h-[480px] w-full overflow-hidden">
        <img src={resolveImg(d.hero)} alt={d.name} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/30" />
        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-16 text-white">
          <Link to="/destinations" className="text-white/80 text-xs uppercase tracking-[0.3em] mb-3 inline-flex items-center gap-2 hover:text-white">
            <ArrowRight className="h-3 w-3 rotate-180" /> Back to destinations
          </Link>
          <div className="text-[11px] uppercase tracking-[0.32em] text-white/80 mb-3 flex items-center gap-2">
            <MapPin className="h-3 w-3" /> {d.state}
          </div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-light max-w-4xl">{d.name}</h1>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-white/85 text-sm">
            <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" /> {d.best}</span>
            <span>·</span>
            <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> {d.duration}</span>
            <span>·</span>
            <span>From ₹{(d.price || 0).toLocaleString("en-IN")} /person</span>
          </div>
        </div>
      </section>

      {/* OVERVIEW + sticky CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-20 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <Reveal>
            <SectionLabel>Overview</SectionLabel>
            <p className="text-lg leading-relaxed text-foreground/85 max-w-3xl">{d.blurb}</p>
          </Reveal>

          {/* Best time + Top attractions + Things to do */}
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            <Reveal>
              <div className="rounded-2xl border border-border bg-card p-6 h-full">
                <div className="text-[11px] uppercase tracking-[0.25em] text-primary mb-2">Best time to visit</div>
                <div className="font-display text-2xl">{d.best}</div>
                <div className="text-sm text-muted-foreground mt-2">Recommended trip duration: {d.duration}</div>
              </div>
            </Reveal>
            <Reveal delay={0.05}>
              <div className="rounded-2xl border border-border bg-card p-6 h-full">
                <div className="text-[11px] uppercase tracking-[0.25em] text-primary mb-3">Top attractions</div>
                <ul className="grid gap-2 text-sm">
                  {(d.attractions || []).map((a) => (
                    <li key={a} className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-primary mt-0.5" /> {a}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>

          <Reveal>
            <div className="mt-6 rounded-2xl border border-border bg-card p-6">
              <div className="text-[11px] uppercase tracking-[0.25em] text-primary mb-3">Things to do</div>
              <ul className="flex flex-wrap gap-2">
                {(d.todo || []).map((t) => (
                  <li key={t} className="text-xs px-3 py-1.5 rounded-full border border-border bg-muted/50">{t}</li>
                ))}
              </ul>
            </div>
          </Reveal>

          {/* Suggested itinerary */}
          <Reveal>
            <div className="mt-14">
              <SectionLabel>Suggested itinerary</SectionLabel>
              <h2 className="font-display text-3xl md:text-4xl font-light mb-8">A pace we&apos;d travel.</h2>
              <ol className="relative border-l border-border pl-8 space-y-8">
                {d.itinerary.map((it) => (
                  <li key={it.day} className="relative">
                    <span className="absolute -left-[37px] top-1 h-6 w-6 rounded-full bg-primary text-primary-foreground text-xs grid place-items-center font-medium">
                      {it.day}
                    </span>
                    <div className="font-display text-xl mb-1">{it.title}</div>
                    <div className="text-muted-foreground leading-relaxed">{it.desc}</div>
                  </li>
                ))}
              </ol>
            </div>
          </Reveal>

          {/* Photo gallery */}
          <Reveal>
            <div className="mt-16">
              <SectionLabel>Photo gallery</SectionLabel>
              <h2 className="font-display text-3xl md:text-4xl font-light mb-8 flex items-center gap-3">
                <Camera className="h-6 w-6 text-primary" /> {d.name} in light
              </h2>
              <div className="columns-2 md:columns-3 gap-4 space-y-4">
                {(d.gallery || []).map((src, i) => (
                  <div key={i} className="overflow-hidden rounded-2xl break-inside-avoid group">
                    <img src={resolveImg(src)} alt={`${d.name} gallery ${i + 1}`} className="w-full transition-transform duration-[1200ms] group-hover:scale-110" loading="lazy" />
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          {/* FAQs */}
          <Reveal>
            <div className="mt-16">
              <SectionLabel>FAQs</SectionLabel>
              <h2 className="font-display text-3xl md:text-4xl font-light mb-6">Good to know.</h2>
              <Accordion type="single" collapsible>
                {(d.faqs || []).map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} data-testid={`dest-faq-${i}`}>
                    <AccordionTrigger className="text-left font-display text-lg">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </Reveal>
        </div>

        {/* STICKY SIDE CARD */}
        <aside className="lg:col-span-1">
          <div className="sticky top-28 space-y-6">
            <div className="rounded-3xl border border-border bg-card p-6">
              <div className="text-[11px] uppercase tracking-[0.25em] text-primary mb-2">Starting from</div>
              <div className="font-display text-4xl mb-1">₹{(d.price || 0).toLocaleString("en-IN")}</div>
              <div className="text-xs text-muted-foreground mb-5">per person · taxes extra</div>
              <Link to={`/booking/${d.packageIds?.[0] || "meg-3"}`}>
                <Button data-testid="detail-book-now" className="rounded-full w-full h-11">Book Now</Button>
              </Link>
              <a href={waUrl} target="_blank" rel="noreferrer" data-testid="detail-whatsapp" className="mt-3 block">
                <Button variant="outline" className="rounded-full w-full h-11 border-emerald-500 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/30">
                  <MessageCircle className="h-4 w-4 mr-2" /> WhatsApp enquiry
                </Button>
              </a>
              <div className="mt-5 pt-5 border-t border-border text-xs text-muted-foreground space-y-2">
                <div className="flex items-center gap-2"><Calendar className="h-3.5 w-3.5 text-primary" /> {d.best}</div>
                <div className="flex items-center gap-2"><Clock className="h-3.5 w-3.5 text-primary" /> {d.duration}</div>
                <div className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-primary" /> {d.state}</div>
              </div>
            </div>

            {/* Map placeholder */}
            <div className="rounded-3xl overflow-hidden border border-border">
              <iframe
                title={`Map of ${d.name}`}
                src={mapSrc}
                className="w-full h-72"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
        </aside>
      </section>

      {/* Available tour packages */}
      {matchingPackages.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 mt-24">
          <Reveal>
            <SectionLabel>Available tour packages</SectionLabel>
            <h2 className="font-display text-3xl md:text-4xl font-light mb-10">Pair {d.name} with a complete journey.</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {matchingPackages.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.06}>
                <div className="group rounded-3xl overflow-hidden border border-border bg-card hover:shadow-2xl transition-shadow duration-500 h-full flex flex-col">
                  <div className="relative h-52 overflow-hidden">
                    <img src={resolveImg(p.cover)} alt={p.title} className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-[1200ms]" />
                    <div className="absolute top-4 left-4 glass rounded-full px-3 py-1 text-xs">{p.days}D · {p.nights}N</div>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="font-display text-xl mb-2">{p.title}</h3>
                    <p className="text-sm text-muted-foreground mb-4">{(p.places || []).join(" · ")}</p>
                    <div className="mt-auto pt-4 border-t border-border flex items-center justify-between">
                      <div>
                        <div className="text-xs text-muted-foreground">From</div>
                        <div className="font-display text-lg">₹{(p.price || 0).toLocaleString("en-IN")}</div>
                      </div>
                      <Link to={`/booking/${p.id}`}>
                        <Button data-testid={`dest-pkg-book-${p.id}`} size="sm" className="rounded-full">Book</Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* Nearby places */}
      {nearbyDests.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 mt-24">
          <Reveal>
            <SectionLabel>Nearby places</SectionLabel>
            <h2 className="font-display text-3xl md:text-4xl font-light mb-10">Close enough to combine.</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {nearbyDests.map((n, i) => (
              <Reveal key={n.slug} delay={i * 0.05}>
                <Link
                  to={`/destinations/${n.slug}`}
                  data-testid={`nearby-${n.slug}`}
                  className="group block rounded-2xl overflow-hidden border border-border bg-card hover:shadow-xl transition-all hover:-translate-y-1 duration-500"
                >
                  <div className="relative h-40 overflow-hidden">
                    <img src={resolveImg(n.hero)} alt={n.name} className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-[1200ms]" />
                  </div>
                  <div className="p-4">
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{n.state}</div>
                    <div className="font-display text-lg">{n.name}</div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
